import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { History, Trophy, Target, Clock, Check, X, MinusCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MARKET_ORDER = [
  "even_balls",
  "first_ball_scoring",
  "even_runs",
  "boundary",
  "six",
  "dot_balls_gt3",
  "wicket",
  "runs_gt7",
];

const MARKET_LABELS = {
  even_balls: "Double Runs",
  first_ball_scoring: "1st Ball Scores",
  even_runs: "Even Over Runs",
  boundary: "Boundary",
  six: "Six",
  dot_balls_gt3: "3+ Dot Balls",
  wicket: "Wicket",
  runs_gt7: "7+ Runs",
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function HistoryPage() {
  const { user, loading, setAuthOpen } = useAuth();
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    api.get("/history/me").then((r) => setGroups(r.data)).finally(() => setFetching(false));
  }, [user, loading]);

  if (loading) return <div className="p-12 text-slate-400">Loading…</div>;

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <div className="tile p-10">
          <h2 className="font-display font-black text-3xl uppercase">Sign in to see your history</h2>
          <p className="text-slate-400 mt-2">All your over-by-over calls, actual outcomes and points earned.</p>
          <button className="btn-primary mt-6" onClick={() => setAuthOpen(true)} data-testid="history-signin-cta">Sign in</button>
        </div>
      </div>
    );
  }

  const totalOvers = groups.length;
  const totalPoints = groups.reduce((s, g) => s + (g.total_points || 0), 0);
  const totalPicks = groups.reduce((s, g) => s + (g.picks_count || 0), 0);
  const totalCorrect = groups.reduce((s, g) => s + (g.correct_count || 0), 0);
  const accuracy = totalPicks > 0 ? Math.round((totalCorrect / totalPicks) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="section-label">Your calls · newest first</div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-1 flex items-center gap-3">
            <History className="w-9 h-9 text-amber-400" /> Prediction History
          </h1>
        </div>
        <Link to="/profile" className="btn-ghost text-xs" data-testid="history-back-profile">← Back to profile</Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={<Clock className="w-4 h-4 text-sky-400" />} label="Overs called" value={totalOvers} testid="history-overs-stat" />
        <Stat icon={<Trophy className="w-4 h-4 text-amber-400" />} label="Points earned" value={totalPoints} testid="history-points-stat" />
        <Stat icon={<Check className="w-4 h-4 text-emerald-400" />} label="Picks correct" value={`${totalCorrect}/${totalPicks}`} testid="history-correct-stat" />
        <Stat icon={<Target className="w-4 h-4 text-purple-400" />} label="Accuracy" value={`${accuracy}%`} testid="history-accuracy-stat" />
      </div>

      {fetching ? (
        <div className="text-slate-400" data-testid="history-loading">Fetching your history…</div>
      ) : groups.length === 0 ? (
        <div className="tile p-10 text-center" data-testid="history-empty">
          <p className="text-slate-300">You haven't locked in any picks yet.</p>
          <p className="text-slate-500 text-sm mt-2">Head to a live match, tap Yes/No on any of the 8 markets, then come back here to see how you fared.</p>
          <Link to="/" className="btn-primary inline-block mt-6">Enter the arena</Link>
        </div>
      ) : (
        <div className="space-y-4 stagger">
          {groups.map((g) => <OverCard key={`${g.match_id}-${g.over_number}`} g={g} />)}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="tile p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">{icon}<span className="section-label">{label}</span></div>
      <div className="metric-value text-2xl">{value}</div>
    </div>
  );
}

function OverCard({ g }) {
  const hasPending = g.pending_count > 0;
  const resolvedAt = g.evaluated_at || g.created_at;
  return (
    <div className="tile p-5 grain" data-testid={`history-over-card-${g.match_id}-${g.over_number}`}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-display font-black uppercase text-xl tracking-tight">{g.match_title || g.match_id} · Over #{g.over_number}</div>
          <div className="text-xs text-slate-500 font-mono-data mt-0.5">{hasPending ? "Locked at" : "Resolved"} · {fmtDate(resolvedAt)}</div>
        </div>
        <div className="flex items-center gap-2">
          {hasPending ? (
            <span className="pill bg-sky-500/15 text-sky-300 border border-sky-500/30">Pending · {g.pending_count} of {g.picks_count}</span>
          ) : (
            <span className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
              <Check className="w-3 h-3" />{g.correct_count}/{g.picks_count} correct
            </span>
          )}
          <span className="pill bg-amber-500/15 text-amber-300 border border-amber-500/40">
            <Trophy className="w-3 h-3" />+{g.total_points} pts
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {MARKET_ORDER.map((mid) => (
          <MarketRow key={mid} label={MARKET_LABELS[mid]} pick={g.picks_by_market[mid]} testidBase={`${g.match_id}-${g.over_number}-${mid}`} />
        ))}
      </div>
    </div>
  );
}

function MarketRow({ label, pick, testidBase }) {
  if (!pick) {
    return (
      <div className="tile-elev p-2.5 opacity-50" data-testid={`history-market-${testidBase}`}>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-data">{label}</div>
        <div className="mt-1 text-xs text-slate-500 flex items-center gap-1"><MinusCircle className="w-3 h-3" /> not picked</div>
      </div>
    );
  }
  const won = pick.status === "won";
  const lost = pick.status === "lost";
  const pending = pick.status === "pending";
  return (
    <div
      className={`tile-elev p-2.5 ${won ? "ring-1 ring-emerald-400/50" : lost ? "ring-1 ring-rose-500/40" : ""}`}
      data-testid={`history-market-${testidBase}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-data">{label}</div>
      <div className="mt-1 flex items-center justify-between">
        <span className={`text-xs font-bold uppercase ${pick.answer === "Yes" ? "text-emerald-300" : "text-rose-300"}`}>{pick.answer}</span>
        <span className="text-[10px] text-slate-500">→</span>
        <span className={`text-xs font-bold ${
          pending ? "text-slate-400" : won ? "text-emerald-300" : "text-rose-300"
        }`}>{pending ? "…" : (pick.correct_answer || "—")}</span>
      </div>
      <div className="mt-1 text-[10px] font-mono-data flex items-center justify-between">
        <span className={won ? "text-emerald-400" : lost ? "text-rose-400" : "text-sky-400"}>
          {won ? <><Check className="w-3 h-3 inline" /> won</> : lost ? <><X className="w-3 h-3 inline" /> missed</> : "pending"}
        </span>
        <span className="text-slate-500">{pick.points_earned ? `+${pick.points_earned}` : ""}</span>
      </div>
    </div>
  );
}
